import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Wand2 } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DicteeBouton, ajouterTexte } from "@/components/assistant/DicteeBouton";
import { ChoixListe } from "@/pages/parametres/ChoixListe";
import { HoteOutil } from "@/components/outils/HoteOutil";
import { Versions, Garder, Reglages } from "@/components/outils/Panneaux";

const EXEMPLES = ["Un calculateur de prime CEE pour les PAC qui reprend les infos de la fiche", "Une vue processus en colonnes avec le montant", "Un thème sombre bleu"];

const Creation = () => {
  const navigate = useNavigate();
  const [d, setD] = useState("");
  const [occ, setOcc] = useState(false);
  const generer = async () => {
    setOcc(true);
    try {
      const { data } = await api.post("/outils", { description: d });
      toast.success(data.message);
      navigate(data.type === "theme" ? "/parametres/themes" : `/outils/${data.outil.id}`);
    } catch (x) { toast.error(messageErreur(x)); } finally { setOcc(false); }
  };
  return (
    <section className="at-card p-5 space-y-3" data-testid="tool-create">
      <p className="font-bold">Décrivez ce que vous voulez</p>
      <div className="flex gap-2 items-start">
        <textarea value={d} onChange={(e) => setD(e.target.value)} rows={3} className="at-input flex-1 min-w-0 py-3" placeholder="Ex. un calculateur de prime CEE pour les PAC…" data-testid="tool-description-input" />
        <DicteeBouton onTexte={(t) => setD((v) => ajouterTexte(v, t))} testId="tool-description-mic" />
      </div>
      <div className="flex flex-wrap gap-2">{EXEMPLES.map((e, i) => <button key={e} type="button" onClick={() => setD(e)} className="text-xs at-pill px-3 min-h-[44px] text-left" style={{ background: "var(--surface-2)" }} data-testid={`tool-example-${i}`}>{e}</button>)}</div>
      <button type="button" onClick={generer} disabled={occ || d.trim().length < 6} className="at-btn at-btn-primary w-full" data-testid="tool-generate-button"><Sparkles size={16} />{occ ? "Génération…" : "Générer l'aperçu"}</button>
      <p className="text-xs at-muted">Génération simulée (API Claude branchée plus tard). Le mot « vue » crée une vue processus, le mot « thème » un thème en brouillon.</p>
    </section>
  );
};

const Contexte = ({ o, ctx, setCtx }) => {
  const [fiches, setFiches] = useState([]);
  const [procs, setProcs] = useState([]);
  useEffect(() => {
    if (o.type === "vue") api.get("/processus").then((r) => { setProcs(r.data); setCtx((c) => ({ ...c, processus_id: c.processus_id || r.data[0]?.id })); });
    else api.get("/fiches").then((r) => setFiches(r.data.slice(0, 60)));
  }, [o.type, setCtx]);
  return o.type === "vue"
    ? <ChoixListe valeur={ctx.processus_id || ""} onChange={(v) => setCtx({ processus_id: v })} options={procs.map((p) => ({ code: p.id, label: p.nom }))} placeholder="Processus" testId="tool-context-process" />
    : <ChoixListe valeur={ctx.fiche_id || "__aucune__"} onChange={(v) => setCtx({ fiche_id: v === "__aucune__" ? null : v })} testId="tool-context-fiche"
        options={[{ code: "__aucune__", label: "Sans fiche ouverte" }, ...fiches.map((f) => ({ code: f.id, label: f.nom }))]} />;
};

const Ajuster = ({ o, onMaj }) => {
  const [c, setC] = useState("");
  const [occ, setOcc] = useState(false);
  const envoyer = async () => {
    setOcc(true);
    try { const { data } = await api.post(`/outils/${o.id}/ajuster`, { consigne: c }); onMaj(data); setC(""); toast.success(`Version ${data.version_active} : ${data.versions.at(-1).notes}`); }
    catch (x) { toast.error(messageErreur(x)); } finally { setOcc(false); }
  };
  return (
    <div className="flex gap-2 items-center" data-testid="tool-adjust">
      <input value={c} onChange={(e) => setC(e.target.value)} onKeyDown={(e) => e.key === "Enter" && c.trim() && envoyer()} className="at-input flex-1 min-w-0" placeholder="« plus grand », « ajoute le département »…" data-testid="tool-adjust-input" />
      <DicteeBouton onTexte={(t) => setC((v) => ajouterTexte(v, t))} testId="tool-adjust-mic" />
      <button type="button" onClick={envoyer} disabled={occ || !c.trim()} className="at-btn at-btn-primary shrink-0 !px-3" aria-label="Ajuster" data-testid="tool-adjust-button"><Wand2 size={16} /></button>
    </div>
  );
};

export default function OutilEditeur() {
  const { id } = useParams();
  const { user } = useAuth();
  const dir = user.role === "dirigeant";
  const [o, setO] = useState(null);
  const [ctx, setCtx] = useState({});
  const charger = useCallback(() => { if (id !== "nouveau") api.get(`/outils/${id}`).then((r) => setO(r.data)).catch((x) => toast.error(messageErreur(x))); }, [id]);
  useEffect(() => { setO(null); charger(); }, [charger]);
  return (
    <div className="space-y-4 min-w-0" data-testid="page-tool-editor">
      <Link to="/outils" className="at-btn at-btn-ghost w-fit" data-testid="tool-back"><ArrowLeft size={16} />Mes outils</Link>
      {id === "nouveau" ? <Creation /> : o && (
        <div className="grid lg:grid-cols-[1fr_340px] gap-4 min-w-0">
          <div className="space-y-3 min-w-0">
            <h1 className="text-3xl font-bold tracking-tight break-words" data-testid="tool-title">{o.nom}</h1>
            <Contexte o={o} ctx={ctx} setCtx={setCtx} />
            <div className="at-card p-2 min-w-0" data-testid="tool-preview"><HoteOutil outil={o} contexte={ctx} hauteur={760} /></div>
            {dir && <Ajuster o={o} onMaj={setO} />}
          </div>
          {dir && (
            <div className="space-y-4 min-w-0">
              {o.statut === "brouillon" ? <Garder o={o} onMaj={setO} /> : <Reglages o={o} onMaj={charger} />}
              <Versions o={o} onMaj={setO} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
